// ============================================================================
// ORGANISATION MASTER TYPES
// ============================================================================
// Entities, business units and financial years of ONE tenant — the organisation
// master (TENANT_ONBOARDING_AND_SETUP_STANDARD.md §7.5). Matches the API's
// EntityResponse / BusinessUnitResponse / FinancialYearResponse and the
// TenantOrganisationController at /tenants/{tenantId}/organisation/{kind}.
//
// A row's `id` is its canonical id. Active / inactive is expressed solely by
// `statusCode` (ORG_ACTIVE / ORG_INACTIVE); there is no isActive flag.

export type OrganisationKind = 'entities' | 'business-units' | 'financial-years';

export type OrganisationStatusCode = 'ORG_ACTIVE' | 'ORG_INACTIVE';

export const ORGANISATION_STATUS_OPTIONS: { value: OrganisationStatusCode; label: string }[] = [
  { value: 'ORG_ACTIVE', label: 'Active' },
  { value: 'ORG_INACTIVE', label: 'Inactive' },
];

/**
 * Entity and business-unit types are free codes (1–50 characters) owned by the application
 * that creates the row: its own lookup table defines and validates them (for example
 * PRIVATE_LIMITED, HEAD_OFFICE in the enterprise templates). The master stores the code
 * as given (tenant-hub-db fixes/007); legacy rows may carry Headquarters, Branch,
 * Subsidiary, Division, Department or Department, Division, Team, CostCenter, Unit.
 */
export const ORGANISATION_TYPE_MAX_LENGTH = 50;

// ─── Responses ────────────────────────────────────────────────────────────────

interface OrganisationRowBase {
  id: string;
  tenantId: string;
  statusId?: string;
  statusCode: OrganisationStatusCode | string;
  /** Organisation master version of the row (org.tbl_organisation_change id). */
  version: number;
  metadata?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EntityResponse extends OrganisationRowBase {
  entityCode: string;
  entityName: string;
  parentEntityId?: string | null;
  /** Read-only projection of the parent row. */
  parentEntityCode?: string | null;
  parentEntityName?: string | null;
  entityType: string;
  address?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
}

export interface BusinessUnitResponse extends OrganisationRowBase {
  entityId: string;
  buCode: string;
  buName: string;
  parentBuId?: string | null;
  /** Read-only projections of the entity and parent rows. */
  entityCode?: string | null;
  entityName?: string | null;
  parentBuCode?: string | null;
  parentBuName?: string | null;
  buType: string;
  headUserId?: string | null;
  description?: string | null;
}

export interface FinancialYearResponse extends OrganisationRowBase {
  fyCode: string;
  fyName: string;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
  isClosed: boolean;
}

// ─── Requests ─────────────────────────────────────────────────────────────────

export interface SaveEntityRequest {
  entityCode: string;
  entityName: string;
  parentEntityId?: string | null;
  entityType: string;
  address?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  statusCode: OrganisationStatusCode;
}

export interface SaveBusinessUnitRequest {
  entityId: string;
  buCode: string;
  buName: string;
  parentBuId?: string | null;
  buType: string;
  headUserId?: string | null;
  description?: string | null;
  statusCode: OrganisationStatusCode;
}

export interface SaveFinancialYearRequest {
  fyCode: string;
  fyName: string;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
  isClosed: boolean;
  statusCode: OrganisationStatusCode;
}

// ─── Search ───────────────────────────────────────────────────────────────────

/** Query of the paged lists. Matches the API's OrganisationSearchRequest. */
export interface OrganisationSearchParams {
  pageNumber?: number;
  pageSize?: number;
  searchTerm?: string;
  statusCode?: OrganisationStatusCode | '';
  /** Business units only. */
  entityId?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}
